"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.formatNumberOrCurrency = exports.formatBigNumber = exports.isBigNumberString = exports.padZeros = exports.trimZeros = exports.fixDecimal = exports.getDecimalSeparator = exports.validateNumberString = exports.validateFractionDigitValue = exports.validateFractionDigits = exports.validateNumberOptionsBasic = exports.validateOptions = exports.transformPercentValue = exports.transformNumberOptions = exports.transformNumberOptionsBasic = exports.transformOptions = exports.getNumberFormatter = exports.MAXIMUM_PRECISE_DIGITS = void 0;
const big_js_1 = require("big.js");
const FormatterUtils_1 = require("../FormatterUtils");
const Number_types_1 = require("./Number.types");
exports.MAXIMUM_PRECISE_DIGITS = 15;
function getNumberFormatter(options) {
    const newOptions = exports.validateOptions(options);
    const intlOptions = exports.transformOptions(newOptions);
    return new Intl.NumberFormat(newOptions.locale, intlOptions);
}
exports.getNumberFormatter = getNumberFormatter;
function transformOptions(options) {
    return Object.assign(Object.assign({}, exports.transformNumberOptionsBasic(options)), exports.transformNumberOptions(options));
}
exports.transformOptions = transformOptions;
function transformNumberOptionsBasic(options) {
    const numberFormatOptions = {};
    numberFormatOptions.minimumFractionDigits = options.minimumFractionDigits;
    numberFormatOptions.maximumFractionDigits = options.maximumFractionDigits;
    numberFormatOptions.notation = options.notation;
    return numberFormatOptions;
}
exports.transformNumberOptionsBasic = transformNumberOptionsBasic;
function transformNumberOptions(options) {
    const numberFormatOptions = {};
    if (options.percent)
        numberFormatOptions.style = "percent";
    return numberFormatOptions;
}
exports.transformNumberOptions = transformNumberOptions;
function transformPercentValue(value, formatter, percent) {
    var _a;
    if (!percent || percent === Number_types_1.Percent.DECIMAL)
        return value.toString();
    const transformed = exports.isBigNumberString(value)
        ? new big_js_1.Big(value).div(100).toString()
        : Number(value) / 100;
    const valueFractionDigits = ((_a = value.toString().split(".")[1]) === null || _a === void 0 ? void 0 : _a.length) || 0;
    const maximumFractionDigits = formatter.resolvedOptions().maximumFractionDigits;
    const fixed = fixDecimal(transformed, (valueFractionDigits < maximumFractionDigits
        ? valueFractionDigits
        : maximumFractionDigits) + 2);
    return /\./.test(fixed)
        ? fixed.replace(/\d+$/, trimZeros(fixed.split(".")[1], formatter.resolvedOptions().minimumFractionDigits))
        : fixed;
}
exports.transformPercentValue = transformPercentValue;
function validateOptions(options) {
    const updatedOptions = Object.assign(Object.assign({}, options), { locale: (0, FormatterUtils_1.getINTLCompatibleLocale)(options.locale) });
    exports.validateNumberOptionsBasic(updatedOptions);
    return updatedOptions;
}
exports.validateOptions = validateOptions;
function validateNumberOptionsBasic(options) {
    exports.validateFractionDigits(options);
    return options;
}
exports.validateNumberOptionsBasic = validateNumberOptionsBasic;
function validateFractionDigits(options) {
    if (options.minimumFractionDigits) {
        exports.validateFractionDigitValue(options.minimumFractionDigits, "minimum");
    }
    if (options.maximumFractionDigits) {
        exports.validateFractionDigitValue(options.maximumFractionDigits, "maximum");
    }
    if (options.minimumFractionDigits &&
        options.maximumFractionDigits &&
        options.minimumFractionDigits > options.maximumFractionDigits) {
        const errorMessage = `@procore/globalization-toolkit Intl.NumberFormat error, minimumFractionDigits must be less than or equal to maximumFractionDigits.`;
        console.error(errorMessage);
        throw new Error(errorMessage);
    }
    return options;
}
exports.validateFractionDigits = validateFractionDigits;
function validateFractionDigitValue(value, minMax) {
    if (!(Number.isInteger(value) && value >= 0 && value <= 20)) {
        const errorMessage = `@procore/globalization-toolkit Intl.NumberFormat error for ${minMax}FractionDigits ${value}. Possible values are integers from 0 to 20.`;
        console.error(errorMessage);
        throw new Error(errorMessage);
    }
    return value;
}
exports.validateFractionDigitValue = validateFractionDigitValue;
function validateNumberString(value) {
    if (Number.isNaN(Number(value))) {
        const errorMessage = `@procore/globalization-toolkit NaN error, string value must represent an unformatted number.`;
        console.error(errorMessage);
        throw new Error(errorMessage);
    }
    return value;
}
exports.validateNumberString = validateNumberString;
function getDecimalSeparator(formatter) {
    var _a;
    return (_a = formatter
        .formatToParts(1e9 + 0.111)
        .find((part) => part.type === "decimal")) === null || _a === void 0 ? void 0 : _a.value;
}
exports.getDecimalSeparator = getDecimalSeparator;
function fixDecimal(value, digits) {
    return isBigNumberString(value)
        ? new big_js_1.Big(value).toFixed(digits)
        : Number(value).toFixed(digits);
}
exports.fixDecimal = fixDecimal;
function trimZeros(value, digits) {
    return value.replace(new RegExp("(\\d{" + digits + "})0+$"), "$1");
}
exports.trimZeros = trimZeros;
function padZeros(value, digits) {
    const trailingDigits = value.match(new RegExp("\\d+$"));
    const length = trailingDigits ? trailingDigits[0].length : 0;
    return value.concat("0".repeat(digits - length < 0 ? 0 : digits - length));
}
exports.padZeros = padZeros;
function isBigNumberString(value) {
    return (typeof value === "string" &&
        value.replace(".", "").length > exports.MAXIMUM_PRECISE_DIGITS);
}
exports.isBigNumberString = isBigNumberString;
function formatBigNumber(value, formatter, percent) {
    const formatFraction = (fixedValue, formatter, percent) => {
        const fraction = percent === Number_types_1.Percent.DECIMAL
            ? fixedValue.split(".")[1].substring(2)
            : fixedValue.split(".")[1];
        const minimumFractionDigits = formatter.resolvedOptions().minimumFractionDigits;
        const zeroAdjust = !fraction || fraction.length < minimumFractionDigits
            ? padZeros(fraction || "", minimumFractionDigits)
            : trimZeros(fraction, minimumFractionDigits);
        return zeroAdjust;
    };
    const extractIntegerValue = (fixedValue, transformed, percent) => {
        if (percent === Number_types_1.Percent.DECIMAL) {
            const fractionPart = fixedValue.split(".")[1] || "";
            const zeroAdjust = fractionPart.length < 2
                ? padZeros(fractionPart, 2)
                : fractionPart.substring(0, 2);
            return transformed.split(".")[0].concat(zeroAdjust);
        }
        return fixedValue.split(".")[0];
    };
    const extractSuffix = (fixedValue, formatter) => {
        const formattedParts = formatter.formatToParts(Number(fixedValue));
        const endOfDigits = formattedParts
            .reverse()
            .findIndex((part) => ["fraction", "decimal", "integer"].includes(part.type));
        const suffixParts = formattedParts.slice(0, endOfDigits).reverse();
        const suffix = suffixParts.reduce((suff, part) => suff + part.value, "");
        return suffix;
    };
    const fixedValue = percent === Number_types_1.Percent.DECIMAL
        ? fixDecimal(value, formatter.resolvedOptions().maximumFractionDigits + 2)
        : fixDecimal(value, formatter.resolvedOptions().maximumFractionDigits);
    const fraction = formatFraction(fixedValue, formatter, percent);
    const transformed = exports.transformPercentValue(fixedValue, formatter, percent);
    const suffix = extractSuffix(fixedValue, formatter);
    const formatted = formatter.format(percent ? transformed : fixedValue);
    const decimalSeparator = exports.getDecimalSeparator(formatter);
    const formattedInteger = (decimalSeparator ? formatted.split(decimalSeparator)[0] : formatted).replace(suffix, "");
    const integerValue = extractIntegerValue(fixedValue, transformed, percent);
    const digits = integerValue.split(".")[0].replace(/[-%]/, "").split("");
    const restoredInteger = formattedInteger.split("").reduce((final, char) => {
        final += /\d/.test(char) && digits.length > 0 ? digits.shift() : char;
        return final;
    }, "");
    const formattedNumber = (fraction
        ? restoredInteger.concat(decimalSeparator).concat(fraction)
        : restoredInteger).concat(suffix);
    return formattedNumber;
}
exports.formatBigNumber = formatBigNumber;
function formatNumberOrCurrency(value, formatter, percent) {
    if (typeof value === "string") {
        exports.validateNumberString(value);
    }
    return exports.isBigNumberString(value)
        ? exports.formatBigNumber(value, formatter, percent)
        : formatter.format(Number(exports.transformPercentValue(value, formatter, percent)));
}
exports.formatNumberOrCurrency = formatNumberOrCurrency;
//# sourceMappingURL=NumberUtils.js.map